/**
 * ADR-0222 — typed dashboard client for the business-skills surface:
 *
 *   GET    /v1/sites/:site_id/business-skills
 *   PUT    /v1/sites/:site_id/business-skills/:skill_id
 *   DELETE /v1/sites/:site_id/business-skills/:skill_id
 *
 * Four slots per site (get_pricing, request_demo, sign_up,
 * book_meeting). Owner/admin only on the write surface — the
 * dashboard pre-hides affordances for reviewer/viewer but the server
 * is the authority.
 */

import { ApiError, type HttpClient } from './http-client.js';

export type BusinessSkillId =
  | 'get_pricing'
  | 'request_demo'
  | 'sign_up'
  | 'book_meeting'
  | 'contact_support'
  | 'get_status'
  | 'get_trust_center';

export const BUSINESS_SKILL_SLOTS: readonly BusinessSkillId[] = [
  'get_pricing',
  'request_demo',
  'sign_up',
  'book_meeting',
  'contact_support',
  'get_status',
  'get_trust_center',
];

export type BusinessPlanPeriod = 'month' | 'year' | 'one_time' | 'custom';

export type BusinessPlanPrice =
  | {
      readonly kind: 'amount';
      readonly amount_decimal: string;
      readonly currency: string;
    }
  | { readonly kind: 'custom' };

export interface BusinessSkillPlan {
  readonly name: string;
  readonly price: BusinessPlanPrice;
  readonly period: BusinessPlanPeriod;
  readonly description?: string;
  readonly sign_up_url?: string;
}

export interface BusinessSkillItem {
  readonly skill_id: BusinessSkillId;
  readonly url: string | null;
  readonly label: string | null;
  readonly enabled: boolean;
  readonly suggested_url: string | null;
  readonly warning: string | null;
  /** ADR-0224 — merchant-maintained plan list for `get_pricing` only;
   *  null for every other slot. */
  readonly plans: readonly BusinessSkillPlan[] | null;
}

export type ListBusinessSkillsResult =
  | { readonly kind: 'ok'; readonly items: readonly BusinessSkillItem[] }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export type PutBusinessSkillResult =
  | { readonly kind: 'ok'; readonly item: BusinessSkillItem }
  | { readonly kind: 'bad_request'; readonly reason: string }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export type DeleteBusinessSkillResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

const SLOT_SET: ReadonlySet<string> = new Set(BUSINESS_SKILL_SLOTS);

function parsePlan(raw: unknown): BusinessSkillPlan | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.name !== 'string' || r.name.length === 0) return null;
  const period = r.period;
  if (
    period !== 'month'
    && period !== 'year'
    && period !== 'one_time'
    && period !== 'custom'
  ) {
    return null;
  }
  let price: BusinessPlanPrice | null = null;
  if (r.price !== null && typeof r.price === 'object' && !Array.isArray(r.price)) {
    const p = r.price as Record<string, unknown>;
    if (p.kind === 'custom') price = { kind: 'custom' };
    else if (
      p.kind === 'amount'
      && typeof p.amount_decimal === 'string'
      && typeof p.currency === 'string'
    ) {
      price = {
        kind: 'amount',
        amount_decimal: p.amount_decimal,
        currency: p.currency,
      };
    }
  }
  if (price === null) return null;
  const plan: {
    name: string;
    price: BusinessPlanPrice;
    period: BusinessPlanPeriod;
    description?: string;
    sign_up_url?: string;
  } = { name: r.name, price, period };
  if (typeof r.description === 'string' && r.description.length > 0) {
    plan.description = r.description;
  }
  if (typeof r.sign_up_url === 'string' && r.sign_up_url.length > 0) {
    plan.sign_up_url = r.sign_up_url;
  }
  return plan;
}

function parseItem(raw: unknown): BusinessSkillItem | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const skill_id = typeof r.skill_id === 'string' ? r.skill_id : '';
  if (!SLOT_SET.has(skill_id)) return null;
  const plans_raw = r.plans;
  const plans: readonly BusinessSkillPlan[] | null = Array.isArray(plans_raw)
    ? (plans_raw.map(parsePlan).filter((p): p is BusinessSkillPlan => p !== null))
    : null;
  return {
    skill_id: skill_id as BusinessSkillId,
    url: typeof r.url === 'string' ? r.url : null,
    label: typeof r.label === 'string' ? r.label : null,
    enabled: r.enabled === true,
    suggested_url: typeof r.suggested_url === 'string' ? r.suggested_url : null,
    warning: typeof r.warning === 'string' ? r.warning : null,
    plans: plans !== null && plans.length === 0 ? null : plans,
  };
}

export async function listBusinessSkills(
  http: HttpClient,
  site_id: string,
): Promise<ListBusinessSkillsResult> {
  try {
    const res = await http.request<Record<string, unknown>>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/business-skills`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const items_raw = Array.isArray(res.items) ? res.items : [];
    const items = items_raw
      .map(parseItem)
      .filter((x): x is BusinessSkillItem => x !== null);
    return { kind: 'ok', items };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 403) return { kind: 'forbidden' };
      if (err.status === 404) return { kind: 'not_found' };
    }
    const message = err instanceof Error ? err.message : 'Request failed.';
    return { kind: 'error', message };
  }
}

export async function putBusinessSkill(
  http: HttpClient,
  site_id: string,
  skill_id: BusinessSkillId,
  body: {
    url?: string | null;
    label?: string | null;
    enabled?: boolean;
    /** ADR-0224 — merchant-maintained plan list; only valid when
     *  `skill_id === 'get_pricing'`. `null` clears the list. */
    plans?: readonly BusinessSkillPlan[] | null;
  },
): Promise<PutBusinessSkillResult> {
  try {
    const res = await http.request<Record<string, unknown>>({
      method: 'PUT',
      path: `/v1/sites/${encodeURIComponent(site_id)}/business-skills/${encodeURIComponent(skill_id)}`,
      body,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    const item = parseItem(res.item);
    if (item === null) return { kind: 'error', message: 'Invalid response shape.' };
    return { kind: 'ok', item };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 403) return { kind: 'forbidden' };
      if (err.status === 404) return { kind: 'not_found' };
      if (err.status === 400) {
        return { kind: 'bad_request', reason: err.server_reason ?? 'bad_request' };
      }
    }
    const message = err instanceof Error ? err.message : 'Request failed.';
    return { kind: 'error', message };
  }
}

export async function deleteBusinessSkill(
  http: HttpClient,
  site_id: string,
  skill_id: BusinessSkillId,
): Promise<DeleteBusinessSkillResult> {
  try {
    await http.request<Record<string, unknown>>({
      method: 'DELETE',
      path: `/v1/sites/${encodeURIComponent(site_id)}/business-skills/${encodeURIComponent(skill_id)}`,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 403) return { kind: 'forbidden' };
      if (err.status === 404) return { kind: 'not_found' };
    }
    const message = err instanceof Error ? err.message : 'Request failed.';
    return { kind: 'error', message };
  }
}
